import React, { PropsWithChildren, useEffect, useState } from 'react';
import './Dashboard.scss';
import { useParams } from 'react-router';
import { DashboardViewModel, type DashboardView } from '@views';
import { DashboardViewCreator, InfoPanelViewCreator, JoyrideContentViewCreator } from '@viewCreators';
import {
  Backdrop,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { DebugPanel, InfoDialog } from '@components';
import { DateTime } from 'luxon';
import logger from '@utils/logger';
import { Joyride, ACTIONS, LIFECYCLE, type EventData, type Step } from 'react-joyride';
import { palette } from '@app/palette';
import { ToDate, ToDateTime } from '@utils/DateUtils';
import { DashboardHeader } from './DashboardHeader';
import { GoalTrackerCard2 } from '@components/GoalTracking';
import { PaymentModal } from '@components/modals';
import { PotCard2 } from '@components/PotCard/PotCard2';
import { PrizesCard } from '@components/Prizes/PrizesCard';
import { ScienceSomethingCard2 } from '@components/ScienceSomething/ScienceSomethingCard2';
import { ExerciseInDisguiseCard2 } from '@components/ExerciseInDeisguise/ExerciseInDisguiseCard2';
import { LeaderCard2 } from '@app/components/Leaderboard/LeaderCard2';
import { SurveyDrawer2 } from '@components/SurveyDrawer/SurveyDrawer2';
import {
  SurveyDrawerContextProvider,
  useSurveyDrawerControllerContext,
} from '@app/components/SurveyDrawer/SurveyDrawerControllerProvider';
import { NavigationEligibilityProvider } from '@components/SurveyDrawer/NavigationEligibilityContextProvider';
import { TeamCard } from '@app/components/TeamCard';
import { useAppCookies } from '@app/providers/AppCookieProvider';

/**
 * Reads the survey drawer's readiness from context (only available inside
 * SurveyDrawerContextProvider) and reports it up to the Dashboard. This lets the Dashboard
 * hold its overlays behind a single "everything's ready" gate without the drawer having to
 * flash open/closed while its section and role queries settle.
 */
const SurveyReadyBridge: React.FC<{ onReadyChange: (ready: boolean) => void }> = ({ onReadyChange }) => {
  const { isReady } = useSurveyDrawerControllerContext();
  useEffect(() => {
    onReadyChange(isReady);
  }, [isReady, onReadyChange]);
  return null;
};

/**
 * Owns both the decision to mount the goal tracker card (and its Goals modal) and the
 * reporting of that overlay's readiness. Keeping the mount predicate in one place means the
 * "should we wait for the goals overlay?" question can never drift from "is the card even
 * rendered?" — change the condition here and both answers move together.
 *
 * When the card renders, GoalTrackerCard2 reports its own readiness through onReadyChange.
 * When it doesn't render, there's nothing to wait on, so this reports ready immediately.
 */
const GoalOverlaySlot: React.FC<{
  view: DashboardView;
  groupStartDate: DateTime | undefined;
  reloadDashboardView: () => void;
  onReadyChange: (ready: boolean) => void;
  onOpenChange: (open: boolean) => void;
}> = ({ view, groupStartDate, reloadDashboardView, onReadyChange, onOpenChange }) => {
  const shouldRender = Boolean(view.cohortUserId && groupStartDate);

  useEffect(() => {
    // No card to wait on (or it just unmounted) — resolve the goals dimension as ready so
    // the dashboard's overlay gate doesn't wait forever on a signal that will never come,
    // and report the modal closed since there's no card to open one.
    if (!shouldRender) {
      onReadyChange(true);
      onOpenChange(false);
    }
  }, [shouldRender, onReadyChange, onOpenChange]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div className="goalsCardJoyride dashboard-card">
      <GoalTrackerCard2
        groupId={view.groupId}
        groupStartDate={groupStartDate as DateTime}
        cohortUserId={view.cohortUserId}
        reloadDashboardView={reloadDashboardView}
        weekNumberIndexZero={view.currentWeekNumberIndexZero}
        onReadyChange={onReadyChange}
        onOpenChange={onOpenChange}
      ></GoalTrackerCard2>
    </div>
  );
};

export const Dashboard: React.FC<PropsWithChildren> = () => {
  const cookies = useAppCookies();
  const params = useParams();
  const [view, setView] = useState(DashboardViewModel.Empty);

  const [groupStartDate, setGroupStartDate] = useState<DateTime>();

  const [reload, setReload] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(true);
  const [forbidden, setForbidden] = useState<boolean>(false);
  const [steps, setSteps] = useState<Step[]>([]);

  const [infoWithDismissedStatus, setInfoWithDismissedStatus] = useState<
    {
      content: any | undefined;
      dismissed: boolean;
      offset: number;
      title: string;
    }[]
  >([]);
  const [infoDialogState, setInfoDialogState] = useState<{
    open: boolean;
    infoDialogContent: { title: string; content: any; offset: number } | undefined;
  }>({ open: false, infoDialogContent: undefined });

  // Readiness of the two overlays whose data loads *after* the main dashboard gate lifts
  // (they fetch once their components mount). We hold a loader over the overlays until both
  // report ready, so nothing flashes on then corrects itself. The other overlays (payment,
  // info panel, joyride) are driven by data already fetched before isLoading clears.
  const [goalsReady, setGoalsReady] = useState<boolean>(false);
  const [surveyDrawerReady, setSurveyDrawerReady] = useState<boolean>(false);
  // Whether the Goals modal is currently open. The info panel is a lower priority than goal
  // setting, so we keep it from stacking on top of the Goals modal (which would also steal
  // the modal's Escape/focus).
  const [goalModalOpen, setGoalModalOpen] = useState<boolean>(false);

  const reloadDashboardView = () => {
    setReload(true);
  };

  useEffect(() => {
    // Check if there's another info item to display
    const nextInfo = infoWithDismissedStatus.filter((info) => !info.dismissed).sort((a, b) => a.offset - b.offset)[0];

    // If there is an info item to display, set the dialog state and open the dialog
    if (nextInfo) {
      setInfoDialogState((prev) => ({
        ...prev,
        open: Boolean(nextInfo.content) && !nextInfo.dismissed,
        infoDialogContent: {
          title: nextInfo.title,
          content: nextInfo.content,
          offset: nextInfo.offset,
        },
      }));
    }
  }, [infoWithDismissedStatus]);

  useEffect(() => {
    if (!reload) return;

    const loadJoyrideContent = () => {
      return new JoyrideContentViewCreator().createView().then(setSteps);
    };

    const loadDashboardView = async () => {
      let result;
      try {
        result = await new DashboardViewCreator().createViewWithArg({
          groupId: Number(params['groupId']),
          userId: String(cookies.profile.id),
        });
      } catch (error: any) {
        logger.error('Failed to load dashboard view', error);
        if (error.message && error.message.includes('You are not authorized to view this group')) {
          setForbidden(true);
        }
        return;
      }
      return result;
    };

    const loadInfoPanel = async (groupStartDate: DateTime, groupId: number) => {
      // Get all the info panel items
      const info = await new InfoPanelViewCreator().createView(ToDateTime(groupStartDate), groupId);

      // Set the info items with their dismissed status in state. This will trigger the info dialog to show if needed
      setInfoWithDismissedStatus(info);
    };

    const initializeViews = async () => {
      const result = await loadDashboardView();
      if (!result) {
        return;
      }

      // promises
      await Promise.all([loadInfoPanel(ToDateTime(result.groupStartDate), result.groupId), loadJoyrideContent()]);

      // Convert the group start date via ToDate (ensures we don't have UTC -> local timezone conversion issues)
      const luxonGroupStartDate = ToDate(result.groupStartDate);

      // Update result with the new group start date
      result.groupStartDate = luxonGroupStartDate;
      setGroupStartDate(luxonGroupStartDate);

      // Decide up front whether the joyride should run for this cohort. Returning users who
      // already skipped/finished it have a localStorage flag; without checking it here the
      // tour would briefly start (run defaults to true) and only stop once the START event
      // fired, flashing the onboarding tour at people who've already seen it.
      const joyrideSkipped = Boolean(localStorage.getItem(`dashboard:joyrideskipped${result.groupId}`));
      setRunJoyride(!joyrideSkipped);

      // Set the view state
      setView(result);

      setIsLoading(false);
    };

    initializeViews();
    setReload(false);
  }, [reload]);

  const backgroundStyles: SxProps = {
    background: '#f4f4f4',
    width: '100vw',
    maxWidth: '100%',
    color: palette.primary,
    paddingBottom: '50px',
  };

  // Starts false; the load effect flips it on only when this cohort hasn't skipped/finished
  // the tour. Defaulting to false avoids a flash of the joyride before that decision is made.
  const [runJoyride, setRunJoyride] = useState<boolean>(false);
  const handleJoyrideCallback = (data: EventData) => {
    const cohortId = view.groupId;
    if (!cohortId || cohortId === 0) {
      // Client is not ready yet
      return;
    }
    const { action, lifecycle, index } = data;

    // If the user skips the joyride, we should skip it next time they visit the app
    if (action === ACTIONS.SKIP) {
      localStorage.setItem(`dashboard:joyrideskipped${cohortId}`, 'true');
      setRunJoyride(false);
      return;
    } else if (lifecycle === LIFECYCLE.COMPLETE && index === steps.length - 1) {
      // Note: Lifecycle is set to complete after each step of the joyride,
      // so we need to use the index to determine if the last step was completed

      // Ensure the user doesn't see the joyride again
      localStorage.setItem(`dashboard:joyrideskipped${cohortId}`, 'true');

      // Set run to false so the joyride doesn't show again. This also triggers the info dialog to show
      setRunJoyride(false);

      return;
    } else if (action === ACTIONS.START) {
      const joyrideSkipped = localStorage.getItem(`dashboard:joyrideskipped${cohortId}`);
      if (joyrideSkipped) {
        setRunJoyride(false);
      }
    }
  };

  useEffect(() => {
    // Trigger the info dialog if the joyride is skipped or ends
    if (!runJoyride && infoDialogState.infoDialogContent) {
      setInfoDialogState((prevState) => ({ ...prevState, open: true }));
    }
  }, [runJoyride, infoDialogState.infoDialogContent]);

  // Both dimensions self-report: GoalOverlaySlot resolves goalsReady (whether or not the
  // goal card mounts), and SurveyReadyBridge resolves surveyDrawerReady. No overlay's mount
  // condition is duplicated here, so the gate can't drift from what actually renders.
  const overlaysReady = goalsReady && surveyDrawerReady;

  if (isLoading) {
    return (
      <Container sx={backgroundStyles}>
        <Stack
          direction={'column'}
          sx={{
            gap: '16px',
          }}
        >
          <DashboardHeader loading={isLoading} view={view} />
          <Typography variant="h6" align="center">
            Loading dashboard...
          </Typography>
        </Stack>
      </Container>
    );
  } else {
    return (
      <NavigationEligibilityProvider>
        <SurveyDrawerContextProvider groupId={view.groupId}>
          <SurveyReadyBridge onReadyChange={setSurveyDrawerReady} />
          {/* Until every overlay's outcome is known, cover the screen with a loader instead
              of letting a modal/drawer/tour flash on and then correct itself. Render it only
              while loading (rather than toggling `open`) so the spinner — and its
              role="progressbar" node — leaves the DOM entirely once ready. MUI's Backdrop
              keeps its children mounted when merely closed, which would otherwise leave a
              lingering progressbar that "wait until no spinners" logic never sees clear. */}
          {!overlaysReady && (
            <Backdrop open sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}>
              <CircularProgress color="inherit" />
            </Backdrop>
          )}
          {/* Hold the joyride until every overlay's outcome is known, so it can't start on
              top of a modal that's about to appear (or vice versa). */}
          {overlaysReady && (
            <Joyride
              continuous={true}
              steps={steps}
              run={runJoyride}
              options={{ buttons: ['skip', 'back', 'close', 'primary'] }}
              styles={{ buttonPrimary: { backgroundColor: palette.primary.main } }}
              onEvent={handleJoyrideCallback}
              locale={{
                last: 'Done',
              }}
            />
          )}

          <Container sx={backgroundStyles}>
            <Stack
              direction={'column'}
              sx={{
                gap: '16px',
              }}
            >
              <DashboardHeader loading={isLoading} view={view} />
              <div className="teamCardJoyride dashboard-card">
                <TeamCard
                  participants={view.team.participants}
                  cohortId={view.groupId}
                  cohortName={view.groupName}
                  accountabilityPartners={view.accountabilityPartners}
                ></TeamCard>
              </div>
              {/* Renders GoalTrackerCard2 once the proper conditions are met */}
              <GoalOverlaySlot
                view={view}
                groupStartDate={groupStartDate}
                reloadDashboardView={reloadDashboardView}
                onReadyChange={setGoalsReady}
                onOpenChange={setGoalModalOpen}
              />

              <div className="potCardJoyride dashboard-card">
                <PotCard2 groupId={view.groupId} groupStartDate={view.groupStartDate}></PotCard2>
              </div>
              {groupStartDate && (
                <div className="exerciseInDisguiseJoyride dashboard-card">
                  <ExerciseInDisguiseCard2 groupStart={view.groupStartDate}></ExerciseInDisguiseCard2>
                </div>
              )}
              {groupStartDate && (
                <div className="scienceSomethingJoyride dashboard-card">
                  <ScienceSomethingCard2 startDate={view.groupStartDate} groupId={view.groupId}></ScienceSomethingCard2>
                </div>
              )}
              <div className="leaderCardJoyride dashboard-card">
                <LeaderCard2 groupId={view.groupId} startDate={view.groupStartDate}></LeaderCard2>
              </div>
              {/* PrizesCard renders nothing - including its own wrapper - until the participant
                  has won a prize or been sent a gift card */}
              {view.cohortUserId > 0 && <PrizesCard cohortUserId={view.cohortUserId}></PrizesCard>}
              <DebugPanel displayItem={view} />
            </Stack>
          </Container>
          {overlaysReady && view.needPayment && view.individualPledgeAmount > 0 && (
            <PaymentModal
              cohortUserId={view.cohortUserId}
              groupId={view.groupId}
              open={view.needPayment}
              amount={view.individualPledgeAmount}
              cohortWeek={view.currentWeekNumberIndexZero}
            />
          )}
          {/* SurveyDrawer2 stays mounted so its controller keeps loading; it renders nothing
              until its own data is ready (isDrawerOpen is false while loading). */}
          <SurveyDrawer2 />

          {/* Show the InfoDialog only once overlays are ready, the Joyride isn't running, and
              the Goals modal isn't open — the info panel is lower priority and must not stack
              on top of goal setting. */}
          {overlaysReady && !runJoyride && !goalModalOpen && (
            <InfoDialog
              message={infoDialogState.infoDialogContent?.content ?? ''}
              title={infoDialogState.infoDialogContent?.title ?? ''}
              onDismiss={() => {
                if (infoDialogState.infoDialogContent && infoDialogState.infoDialogContent.title) {
                  localStorage.setItem(
                    `dashboard:info:${infoDialogState.infoDialogContent.title}|${view.groupId}`,
                    'true'
                  );
                }
                setInfoDialogState({ ...infoDialogState, open: false });
                setInfoWithDismissedStatus((prev) =>
                  prev.map((info) => {
                    if (info.title === infoDialogState.infoDialogContent?.title) {
                      return { ...info, dismissed: true };
                    }
                    return info;
                  })
                );
              }}
              onClose={() => {
                setInfoDialogState({ ...infoDialogState, open: false });
              }}
              onReadLater={() => setInfoDialogState({ ...infoDialogState, open: false })}
              open={infoDialogState.open}
            ></InfoDialog>
          )}
          {forbidden && (
            <Dialog
              onClose={() => {
                setForbidden(false);
                window.location.href = '/teams';
              }}
              open={forbidden}
            >
              <DialogTitle>OOPS!</DialogTitle>
              <DialogContent>
                <Typography>
                  You requested to stop or pause your membership on this team. If we got that wrong, email{' '}
                  <a href="mailto:admin@wehateexercise.com">admin@wehateexercise.com</a> so we can make it right!
                </Typography>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'center' }}>
                <Button
                  variant={'contained'}
                  onClick={() => {
                    setForbidden(false);
                    window.location.href = '/teams';
                  }}
                >
                  OK
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </SurveyDrawerContextProvider>
      </NavigationEligibilityProvider>
    );
  } // <-- Ends else block for isLoading check
};
